import { ACTIVITY_CATEGORIES, PARTICIPATIONS, type ContentLang } from '@yoyoclub/shared';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { BilingualField } from '../../components/BilingualField';
import { TranslateAllButton, TranslateAllProvider } from '../../components/TranslateAll';
import AdminLayout from '../../components/admin/AdminLayout';
import { AdminPickIntake } from '../../components/admin/AdminPickIntake';
import {
  emptyBilingual,
  toBilingualValue,
  type BilingualValue,
} from '../../components/bilingualValue';
import { dateRowToOccurrence, splitByScript, type DateRow } from '../../components/picks/pickForm';
import { adminPicksApi, type AdminPick, type AdminPickPayload } from '../../services/adminPicksApi';
import { discoveryApi, type PickCandidate } from '../../services/discoveryApi';
import {
  intakeApi,
  LOW_CONFIDENCE,
  type IntakeField,
  type IntakeJob,
} from '../../services/intakeApi';
import { settlePendingJob } from '../../utils/runIntake';
import { ptInstantParts } from '../../utils/timezone';

const REGIONS = ['SOUTH_BAY', 'PENINSULA', 'EAST_BAY', 'SAN_FRANCISCO', 'OTHER'] as const;
const LANGUAGES = ['ZH', 'EN', 'BILINGUAL', 'NA'] as const;
const LANGUAGE_LABEL: Record<(typeof LANGUAGES)[number], string> = {
  ZH: 'Chinese',
  EN: 'English',
  BILINGUAL: 'Bilingual',
  NA: 'Not language-based',
};
const PARTICIPATION_LABEL: Record<string, string> = {
  PARENT_CHILD: 'Parent and child',
  KID_ONLY: 'Kids only',
  ADULT_ONLY: 'Adults only',
};
const REGION_LABEL: Record<string, string> = {
  SOUTH_BAY: 'South Bay',
  PENINSULA: 'Peninsula',
  EAST_BAY: 'East Bay',
  SAN_FRANCISCO: 'San Francisco',
  OTHER: 'Other',
};

const input =
  'w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary';
const label = 'mb-1 block text-sm font-medium text-gray-700';

type PairKey =
  | 'title'
  | 'description'
  | 'whyPicked'
  | 'externalBookingNote'
  | 'priceNote'
  | 'scheduleNote'
  | 'city';

function pairOf(p: AdminPick | null, key: PairKey): BilingualValue {
  if (!p) return emptyBilingual();
  const r = p as unknown as Record<string, string | null>;
  return toBilingualValue({
    zh: r[`${key}CN`],
    en: r[`${key}EN`],
    autoLang: r[`${key}AutoLang`] as ContentLang | null,
  });
}

function pairPayload(v: BilingualValue, key: PairKey) {
  const zh = v.zh.trim() || null;
  const en = v.en.trim() || null;
  const autoLang = (v.autoLang === 'ZH' && zh) || (v.autoLang === 'EN' && en) ? v.autoLang : null;
  return { [`${key}CN`]: zh, [`${key}EN`]: en, [`${key}AutoLang`]: autoLang };
}

/** A date row that keeps the date's notes, which this form does not edit. */
type AdminDateRow = DateRow & { noteCN: string | null; noteEN: string | null };
const emptyRow = (): AdminDateRow => ({ date: '', start: '', end: '', noteCN: null, noteEN: null });

/** Stored UTC instants back to California date and times for the date rows. */
function toDateRow(o: {
  startTime: string;
  endTime: string;
  noteCN: string | null;
  noteEN: string | null;
}): AdminDateRow {
  const s = ptInstantParts(new Date(o.startTime));
  const e = ptInstantParts(new Date(o.endTime));
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${s.year}-${pad(s.month)}-${pad(s.day)}`,
    start: `${pad(s.hour)}:${pad(s.minute)}`,
    end: `${pad(e.hour)}:${pad(e.minute)}`,
    noteCN: o.noteCN,
    noteEN: o.noteEN,
  };
}

/**
 * /admin/picks/new and /admin/picks/:id (PRD §5.8): the full Pick form in the
 * existing admin style. Saving a published Pick re-checks the publish contract.
 */
export default function AdminPickForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  // /admin/picks/new?candidate=<id>: review a weekly discovery candidate (PRD §6.4).
  const [params] = useSearchParams();
  const candidateId = isNew ? params.get('candidate') : null;
  const [candidate, setCandidate] = useState<PickCandidate | null>(null);
  const [pick, setPick] = useState<AdminPick | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [type, setType] = useState<'PROVIDER' | 'EVENT'>('PROVIDER');
  const [pairs, setPairs] = useState<Record<PairKey, BilingualValue>>({
    title: emptyBilingual(),
    description: emptyBilingual(),
    whyPicked: emptyBilingual(),
    externalBookingNote: emptyBilingual(),
    priceNote: emptyBilingual(),
    scheduleNote: emptyBilingual(),
    city: emptyBilingual(),
  });
  const [providerName, setProviderName] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [region, setRegion] = useState<string[]>([]);
  const [addressLine, setAddressLine] = useState('');
  const [ageMin, setAgeMin] = useState('0');
  const [ageMax, setAgeMax] = useState('12');
  const [participation, setParticipation] = useState('PARENT_CHILD');
  const [language, setLanguage] = useState('EN');
  const [category, setCategory] = useState('OTHER');
  const [dates, setDates] = useState<AdminDateRow[]>([]);
  // Smart intake on a new Pick: the team draft it wrote, saved over instead of created.
  const [draftId, setDraftId] = useState<string | null>(null);
  const [intakeSummary, setIntakeSummary] = useState<string | null>(null);
  // "Saved." / "Published.", also carried over from the new-Pick page to its edit page.
  const location = useLocation();
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice ?? null
  );
  // A web-search draft (the page would not open): where it was read from.
  const [searchSources, setSearchSources] = useState<string[] | null>(null);
  // The form is locked while intake runs, so its result never lands on typed input.
  const [intakeBusy, setIntakeBusy] = useState(false);
  // A timed-out job whose abandon was never confirmed; settled before saving.
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) return;
    adminPicksApi
      .get(id!)
      .then((p) => {
        setPick(p);
        const sources = p.source?.searchSources ?? [];
        if (sources.length) setSearchSources(sources);
        setType(p.type);
        setPairs({
          title: pairOf(p, 'title'),
          description: pairOf(p, 'description'),
          whyPicked: pairOf(p, 'whyPicked'),
          externalBookingNote: pairOf(p, 'externalBookingNote'),
          priceNote: pairOf(p, 'priceNote'),
          scheduleNote: pairOf(p, 'scheduleNote'),
          city:
            p.cityCN || p.cityEN || !p.city
              ? pairOf(p, 'city')
              : toBilingualValue({
                  zh: splitByScript(p.city).cn,
                  en: splitByScript(p.city).en,
                  autoLang: null,
                }),
        });
        setProviderName(p.providerName);
        setExternalUrl(p.externalUrl);
        setRegion(p.region);
        setAddressLine(p.addressLine ?? '');
        setAgeMin(String(p.ageMin));
        setAgeMax(String(p.ageMax));
        setParticipation(p.participation);
        setLanguage(p.language);
        setCategory(p.category);
        setDates(p.occurrences.map(toDateRow));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const setPair = (key: PairKey) => (v: BilingualValue) => setPairs((p) => ({ ...p, [key]: v }));

  /** Prefills the form from an intake draft and lists what needs a look. */
  const applyDraft = (
    job: Pick<IntakeJob, 'draft' | 'confidences' | 'draftPickId'> &
      Partial<Pick<IntakeJob, 'viaSearch' | 'sources'>>,
    reasonNote = 'Why we picked it is always written by the team.'
  ) => {
    const d = job.draft;
    const c = job.confidences;
    if (!d || !c) return;
    const check: string[] = [];
    const missing: string[] = [];
    const filled = (k: IntakeField) => {
      const v = d[k];
      return v !== null && !(Array.isArray(v) && v.length === 0);
    };
    // Unsure when any filled part is unsure: a sure ageMax never hides an unsure ageMin.
    const note = (name: string, has: boolean, keys: IntakeField[]) => {
      const parts = keys.filter(filled);
      if (!has) missing.push(name);
      else if (parts.length && Math.min(...parts.map((k) => c[k] ?? 0)) < LOW_CONFIDENCE) {
        check.push(name);
      }
    };
    // Only values the draft has are written; nothing already typed is cleared.
    if (d.type) setType(d.type);
    note('type', !!d.type, ['type']);
    const bilingual = (zh: string | null, en: string | null) =>
      toBilingualValue({ zh, en, autoLang: null });
    const citySides = splitByScript(d.city ?? '');
    const pairsFrom = {
      title: [d.titleCN, d.titleEN],
      city: [citySides.cn, citySides.en],
      description: [d.descriptionCN, d.descriptionEN],
      priceNote: [d.priceNoteCN, d.priceNoteEN],
      scheduleNote: [d.scheduleNoteCN, d.scheduleNoteEN],
    } as const;
    setPairs((p) => {
      const next = { ...p };
      for (const [key, [zh, en]] of Object.entries(pairsFrom)) {
        if (zh || en) next[key as keyof typeof pairsFrom] = bilingual(zh, en);
      }
      return next;
    });
    note('title', !!(d.titleCN || d.titleEN), ['titleCN', 'titleEN']);
    if (d.providerName) setProviderName(d.providerName);
    note('provider', !!d.providerName, ['providerName']);
    if (d.externalUrl) setExternalUrl(d.externalUrl);
    note('link', !!d.externalUrl, ['externalUrl']);
    if (d.region) setRegion([d.region]);
    note('region', !!d.region, ['region']);
    if (d.addressLine) setAddressLine(d.addressLine);
    note('address', true, ['addressLine']);
    if (d.ageMin !== null && d.ageMax !== null) {
      setAgeMin(String(d.ageMin));
      setAgeMax(String(d.ageMax));
    }
    note('ages', d.ageMin !== null && d.ageMax !== null, ['ageMin', 'ageMax']);
    if (d.participation) setParticipation(d.participation);
    note('participation', !!d.participation, ['participation']);
    if (d.language) setLanguage(d.language);
    note('language', !!d.language, ['language']);
    if (d.category) setCategory(d.category);
    note('category', !!d.category, ['category']);
    if ((d.type ?? type) === 'EVENT') {
      if (d.occurrences.length) {
        setDates(
          d.occurrences.map((o) => ({
            ...emptyRow(),
            date: o.date,
            start: o.start,
            end: o.end ?? '',
          }))
        );
      }
      note('dates', d.occurrences.length > 0, ['occurrences']);
    }
    setDraftId(job.draftPickId);
    setSearchSources(job.viaSearch ? (job.sources ?? []) : null);
    setIntakeSummary(
      [
        check.length ? `Please check: ${check.join(', ')}.` : '',
        missing.length ? `Not found: ${missing.join(', ')}.` : '',
        reasonNote,
      ]
        .filter(Boolean)
        .join(' ')
    );
  };

  useEffect(() => {
    if (!candidateId) return;
    let live = true;
    discoveryApi
      .candidate(candidateId)
      .then((c) => {
        if (!live) return;
        setCandidate(c);
        const { confidences, ...draft } = c.draft;
        applyDraft(
          { draft, confidences, draftPickId: null },
          'Why we picked it below is a draft from the search; rewrite it before publishing.'
        );
        setPairs((p) => ({
          ...p,
          whyPicked: toBilingualValue({
            zh: c.whyPickedDraftCN,
            en: c.whyPickedDraftEN,
            autoLang: null,
          }),
        }));
      })
      .catch((err) => live && setError(err instanceof Error ? err.message : 'Failed to load'));
    return () => {
      live = false;
    };
    // applyDraft only sets state; the candidate is loaded once per id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  const payload = (): AdminPickPayload | null => {
    const occurrences = dates.map(dateRowToOccurrence);
    if (type === 'EVENT' && occurrences.some((o) => o === null)) {
      setError('Every date needs a day, a start and an end time');
      return null;
    }
    return {
      type,
      ...pairPayload(pairs.title, 'title'),
      ...pairPayload(pairs.description, 'description'),
      ...pairPayload(pairs.whyPicked, 'whyPicked'),
      ...pairPayload(pairs.externalBookingNote, 'externalBookingNote'),
      ...pairPayload(pairs.priceNote, 'priceNote'),
      ...pairPayload(pairs.scheduleNote, 'scheduleNote'),
      providerName,
      externalUrl: externalUrl.trim(),
      region,
      ...pairPayload(pairs.city, 'city'),
      addressLine: addressLine || null,
      ageMin: Number(ageMin),
      ageMax: Number(ageMax),
      participation,
      language,
      category,
      occurrences:
        type === 'EVENT'
          ? occurrences.map((o, i) => ({
              ...(o as NonNullable<typeof o>),
              noteCN: dates[i].noteCN,
              noteEN: dates[i].noteEN,
            }))
          : [],
    } as AdminPickPayload;
  };

  const save = async (publish: boolean) => {
    setError(null);
    setNotice(null);
    const doneText = publish ? 'Published.' : 'Saved.';
    const body = payload();
    if (!body) return;
    setSaving(true);
    try {
      if (candidateId) {
        // Approving publishes the Pick and closes the candidate in one step.
        await discoveryApi.approve(candidateId, body);
        navigate('/admin/picks/candidates', { replace: true });
        return;
      }
      // Settle a job whose abandon never landed, so its late draft is not a second one.
      let intoDraft = draftId;
      if (isNew && !intoDraft && pendingJobId) {
        intoDraft = await settlePendingJob(intakeApi('team'), pendingJobId);
        setPendingJobId(null);
        if (intoDraft) setDraftId(intoDraft);
      }
      if (isNew && intoDraft) {
        // The intake already wrote a team draft; save over it rather than add a second.
        await adminPicksApi.update(intoDraft, body);
        if (publish) await adminPicksApi.setStatus(intoDraft, 'PUBLISHED');
        setNotice(doneText);
        navigate(`/admin/picks/${intoDraft}`, { replace: true, state: { notice: doneText } });
      } else if (isNew) {
        const created = await adminPicksApi.create(body, publish);
        setNotice(doneText);
        navigate(`/admin/picks/${created.id}`, { replace: true, state: { notice: doneText } });
      } else {
        // Saving stays on the page; publishing a parent's suggestion approves it, which
        // also tells them it is live.
        await adminPicksApi.update(id!, body);
        if (publish) {
          if (pick?.submissionStatus === 'PENDING') await adminPicksApi.approve(id!);
          else await adminPicksApi.setStatus(id!, 'PUBLISHED');
        }
        setPick(await adminPicksApi.get(id!));
        setNotice(doneText);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const inQueue = pick?.submissionStatus === 'PENDING';

  return (
    <TranslateAllProvider>
      <AdminLayout
        title={candidateId ? 'Review candidate' : isNew ? 'New pick' : 'Edit pick'}
        headerRight={
          <Link
            to={inQueue ? '/admin/picks/submissions' : '/admin/picks'}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
          >
            Back to list
          </Link>
        }
      >
        {loading ? (
          <p className="text-gray-600">Loading...</p>
        ) : (
          <div className="space-y-6 rounded-lg bg-white p-6 shadow-sm">
            <TranslateAllButton className="justify-end" disabled={intakeBusy} />
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}
            {isNew && !draftId && !candidateId && (
              <AdminPickIntake
                onDraft={applyDraft}
                onBusyChange={setIntakeBusy}
                onUnresolved={(u) => {
                  if (u.draftPickId) setDraftId(u.draftPickId);
                  if (u.pendingJobId) setPendingJobId(u.pendingJobId);
                }}
                beforeStart={async () => {
                  if (!pendingJobId) return true;
                  try {
                    const written = await settlePendingJob(intakeApi('team'), pendingJobId);
                    setPendingJobId(null);
                    // The earlier job wrote a draft: save over it instead of starting another.
                    if (written) setDraftId(written);
                    return !written;
                  } catch {
                    setError('Could not reach the server. Try again.');
                    return false;
                  }
                }}
              />
            )}
            {candidate?.reviewSummary && (
              <div className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                  What reviews say (team only, never published)
                </div>
                {candidate.reviewSummary}
              </div>
            )}
            {intakeSummary && (
              <div className="rounded-lg bg-yellow-pale p-3 text-sm text-gray-800">
                {intakeSummary}
              </div>
            )}
            {searchSources && (
              <div className="rounded-lg bg-yellow-pale p-3 text-sm text-gray-800">
                <p>
                  The page would not open, so the form was filled from a web search. Check every
                  field; prices and schedules may be out of date.
                </p>
                {searchSources.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-gray-600">
                    {searchSources.map((u) => (
                      <li key={u} className="break-all">
                        <a
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="underline"
                        >
                          {u}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <fieldset disabled={intakeBusy} className="min-w-0 space-y-6">
              {pick?.submittedBy && (
                <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                  Suggested by {pick.submittedBy.name || pick.submittedBy.email} (
                  {pick.submittedBy.email})
                  {pick.submitterNote ? ` · Note: ${pick.submitterNote}` : ''}
                </div>
              )}
              <div>
                <span className={label}>Type</span>
                <div className="flex gap-4 text-sm">
                  {(['PROVIDER', 'EVENT'] as const).map((v) => (
                    <label key={v} className="inline-flex items-center gap-2">
                      <input type="radio" checked={type === v} onChange={() => setType(v)} />
                      {v === 'PROVIDER' ? 'Provider (ongoing classes)' : 'Event (specific dates)'}
                    </label>
                  ))}
                </div>
              </div>
              <BilingualField
                fieldType="activityTitle"
                label="Title"
                required
                value={pairs.title}
                onChange={setPair('title')}
              />
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className={label} htmlFor="pk-provider">
                    Provider name
                  </label>
                  <input
                    id="pk-provider"
                    className={input}
                    value={providerName}
                    onChange={(e) => setProviderName(e.target.value)}
                  />
                </div>
                <div>
                  <label className={label} htmlFor="pk-url">
                    Link (provider page)
                  </label>
                  <input
                    id="pk-url"
                    className={input}
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    placeholder="https://"
                  />
                </div>
              </div>
              <BilingualField
                fieldType="activityDescription"
                label="Why we picked it"
                required
                multiline
                rows={3}
                value={pairs.whyPicked}
                onChange={setPair('whyPicked')}
              />
              {type === 'PROVIDER' ? (
                <BilingualField
                  fieldType="sessionDescription"
                  label="Schedule"
                  required
                  value={pairs.scheduleNote}
                  onChange={setPair('scheduleNote')}
                />
              ) : (
                <div>
                  <span className={label}>Dates (California time)</span>
                  <div className="space-y-2">
                    {dates.map((row, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2">
                        <input
                          type="date"
                          className={`${input} w-auto`}
                          value={row.date}
                          onChange={(e) =>
                            setDates((d) =>
                              d.map((r, j) => (j === i ? { ...r, date: e.target.value } : r))
                            )
                          }
                        />
                        <input
                          type="time"
                          className={`${input} w-auto`}
                          value={row.start}
                          onChange={(e) =>
                            setDates((d) =>
                              d.map((r, j) => (j === i ? { ...r, start: e.target.value } : r))
                            )
                          }
                        />
                        <span className="text-gray-500">to</span>
                        <input
                          type="time"
                          className={`${input} w-auto`}
                          value={row.end}
                          onChange={(e) =>
                            setDates((d) =>
                              d.map((r, j) => (j === i ? { ...r, end: e.target.value } : r))
                            )
                          }
                        />
                        <button
                          type="button"
                          className="text-sm text-red-600 hover:underline"
                          onClick={() => setDates((d) => d.filter((_, j) => j !== i))}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="text-sm font-medium text-primary hover:underline"
                      onClick={() => setDates((d) => [...d, emptyRow()])}
                    >
                      Add a date
                    </button>
                  </div>
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <span className={label}>Regions</span>
                  <div className="flex flex-wrap gap-3 text-sm">
                    {REGIONS.map((r) => (
                      <label key={r} className="inline-flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={region.includes(r)}
                          onChange={(e) =>
                            setRegion((cur) =>
                              e.target.checked ? [...cur, r] : cur.filter((x) => x !== r)
                            )
                          }
                        />
                        {REGION_LABEL[r]}
                      </label>
                    ))}
                  </div>
                </div>
                <BilingualField
                  fieldType="placeName"
                  label="City"
                  stacked
                  value={pairs.city}
                  onChange={setPair('city')}
                />
                <div>
                  <label className={label} htmlFor="pk-addr">
                    Address (optional, enables the map link)
                  </label>
                  <input
                    id="pk-addr"
                    className={input}
                    value={addressLine}
                    onChange={(e) => setAddressLine(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-5">
                <div>
                  <label className={label} htmlFor="pk-amin">
                    Age from
                  </label>
                  <input
                    id="pk-amin"
                    type="number"
                    min={0}
                    max={99}
                    step={0.5}
                    className={input}
                    value={ageMin}
                    onChange={(e) => setAgeMin(e.target.value)}
                  />
                </div>
                <div>
                  <label className={label} htmlFor="pk-amax">
                    Age to (99 = no limit)
                  </label>
                  <input
                    id="pk-amax"
                    type="number"
                    min={0}
                    max={99}
                    step={0.5}
                    className={input}
                    value={ageMax}
                    onChange={(e) => setAgeMax(e.target.value)}
                  />
                </div>
                <div>
                  <label className={label} htmlFor="pk-part">
                    Who takes part
                  </label>
                  <select
                    id="pk-part"
                    className={input}
                    value={participation}
                    onChange={(e) => setParticipation(e.target.value)}
                  >
                    {PARTICIPATIONS.map((p) => (
                      <option key={p} value={p}>
                        {PARTICIPATION_LABEL[p]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="pk-lang">
                    Language
                  </label>
                  <select
                    id="pk-lang"
                    className={input}
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l} value={l}>
                        {LANGUAGE_LABEL[l]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="pk-cat">
                    Category
                  </label>
                  <select
                    id="pk-cat"
                    className={input}
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {ACTIVITY_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.labelEN}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <BilingualField
                fieldType="sessionDescription"
                label="Price"
                value={pairs.priceNote}
                onChange={setPair('priceNote')}
              />
              <BilingualField
                fieldType="activityDescription"
                label="About"
                multiline
                rows={4}
                value={pairs.description}
                onChange={setPair('description')}
              />
              <BilingualField
                fieldType="activityDescription"
                label="How to book"
                multiline
                rows={2}
                value={pairs.externalBookingNote}
                onChange={setPair('externalBookingNote')}
              />
              <div className="flex flex-wrap items-center gap-3">
                {candidateId ? (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void save(true)}
                    className="rounded-lg bg-primary px-4 py-2 text-white hover:bg-primary-dark disabled:opacity-50"
                  >
                    Approve and publish
                  </button>
                ) : pick?.status === 'PUBLISHED' ? (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void save(false)}
                    className="rounded-lg bg-primary px-4 py-2 text-white hover:bg-primary-dark disabled:opacity-50"
                  >
                    Save changes
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void save(false)}
                      className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {pick?.status === 'ARCHIVED' ? 'Save' : 'Save as draft'}
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void save(true)}
                      className="rounded-lg bg-primary px-4 py-2 text-white hover:bg-primary-dark disabled:opacity-50"
                    >
                      Save and publish
                    </button>
                  </>
                )}
                {notice && (
                  <span role="status" className="text-sm text-green-700">
                    {notice}
                  </span>
                )}
              </div>
            </fieldset>
          </div>
        )}
      </AdminLayout>
    </TranslateAllProvider>
  );
}
